import { yupResolver } from "@hookform/resolvers/yup";
import Select from "react-select";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { NOTE_REASON_TYPE, STATUS } from "constants/order";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Label,
  Modal,
  Row,
} from "reactstrap";
import * as yup from "yup";
import TextArea from "antd/lib/input/TextArea";
import { useListNoteOrderReason, useNoteOrder, useGetOrderNotes } from "hooks";

const WarningNote = ({ orderData }) => {
  const { t } = useTranslation(["order"]);
  const [isOpenModalWarningNote, setIsOpenModalWarningNote] = useState(false);
  const [orderNotesData, setOrderNotesData] = useState(null);
  const maxLengthOfNoteDescription = 255;

  const initDefaultValues = {
    reason_noted_id: undefined,
    noted_description: undefined,
  };

  const schema = yup.object().shape({
    reason_noted_id: yup
      .object()
      .required(
        t("form.validate.select", { title: t("form.reason_noted_id") })
      ),
    noted_description: yup
      .string()
      .max(
        maxLengthOfNoteDescription,
        t("form.validate.char_max", { max: maxLengthOfNoteDescription })
      )
      .when("reason_noted_id", {
        is: reason_noted_id => reason_noted_id.note_required,
        then: yup
          .string()
          .required(
            t("form.validate.required", { title: t("form.noted_description") })
          ),
        otherwise: yup.string(),
      }),
  });

  const {
    formState: { errors },
    control,
    getValues,
    trigger,
    setValue,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: initDefaultValues,
  });

  const { data: listReason, refetch: fetchListNoteOrderReason } =
    useListNoteOrderReason({
      params: {
        "filter[type]": 4,
      },
    });

  const { mutate: noteOrderMutate, isLoading: isLoadingNoteOrderMutate } =
    useNoteOrder(() => {
      toggleWarningNoteModal();
    });

  const { refetch: refetchOrderNotes, isLoading: isLoadingOrderNotes } =
    useGetOrderNotes(
      { order_id: orderData?.id },
      data => {
        setOrderNotesData(data);
      },
      error => {
        console.error("Error fetching order notes:", error);
      }
    );

  const isShowBtnWarningNote = useMemo(() => {
    if (orderData) {
      if (
        [STATUS.CANCEL, STATUS.DONE, STATUS.DELIVERED].includes(
          orderData?.status
        )
      ) {
        return false;
      }
      return true;
    }

    return false;
  }, [orderData]);

  const toggleWarningNoteModal = () => {
    setIsOpenModalWarningNote(prev => !prev);
  };

  useEffect(() => {
    if (isOpenModalWarningNote) {
      fetchListNoteOrderReason();

      if (orderData?.is_noted && orderData?.id) {
        refetchOrderNotes();
      }
    } else {
      reset();
      setOrderNotesData(null);
    }
  }, [isOpenModalWarningNote, orderData?.is_noted, orderData?.id]);

  const optionsSelectReason = useMemo(() => {
    if (listReason && listReason.items && listReason.items.length > 0) {
      return listReason.items.map(item => ({
        label: item.reason,
        value: item.id,
        note_required: Boolean(item.note_required),
      }));
    }

    return [];
  }, [listReason]);

  const onSubmitWarningNote = e => {
    e.preventDefault();

    trigger().then(res => {
      if (res) {
        const values = getValues();
        const data = {
          noted_description: values?.noted_description,
          reason_noted_id: values?.reason_noted_id.value,
        };

        noteOrderMutate({
          order_id: orderData.id,
          data,
        });
      }
    });
  };

  const isHaveDefaultWarningNote = useMemo(() => {
    if (orderNotesData || orderData) {
      const data = orderNotesData || orderData;
      return (
        Boolean(data.is_noted) &&
        data.noted_reason_type !== NOTE_REASON_TYPE.HANDLE
      );
    }

    return false;
  }, [orderNotesData, orderData]);

  useEffect(() => {
    const data = orderNotesData || orderData;

    if (
      data &&
      isOpenModalWarningNote &&
      !isHaveDefaultWarningNote &&
      data.is_noted
    ) {
      const reason = optionsSelectReason.find(
        item => item.value === data?.reason_noted_id
      );

      if (reason) {
        setValue("reason_noted_id", reason);
      }
      setValue("noted_description", data.noted_description);
    }
  }, [
    orderNotesData,
    orderData,
    isHaveDefaultWarningNote,
    isOpenModalWarningNote,
    optionsSelectReason,
    setValue,
  ]);

  const isNoteDescriptionRequired = watch("reason_noted_id")?.note_required;
  const messageLength = watch("noted_description")?.length;

  const characterRemain = useMemo(() => {
    let colorText = "text-success";
    if (messageLength >= maxLengthOfNoteDescription) {
      colorText = "text-danger";
    } else if (messageLength > 230) {
      colorText = "text-warning";
    }
    return (
      <span className={colorText}>
        {messageLength
          ? messageLength < 255
            ? maxLengthOfNoteDescription - messageLength
            : 0
          : maxLengthOfNoteDescription}
      </span>
    );
  }, [messageLength]);

  return (
    isShowBtnWarningNote && (
      <>
        <Button color="warning" onClick={toggleWarningNoteModal}>
          {t("detail.warningNoteBtn")}
        </Button>
        <Modal
          isOpen={isOpenModalWarningNote}
          centered
          toggle={toggleWarningNoteModal}
        >
          <Form className="p-4" onSubmit={onSubmitWarningNote}>
            {isLoadingOrderNotes && (
              <Row>
                <Col>Đang tải dữ liệu lưu ý...</Col>
              </Row>
            )}
            {isHaveDefaultWarningNote && (
              <Row>
                <Alert color="warning">
                  Đơn này đã có lưu ý mặc định. Nếu bạn gửi lưu ý mới thì sẽ
                  được ghi đè lên lưu ý mặc định !
                </Alert>
              </Row>
            )}
            <Row>
              <FormGroup className="gy-2">
                <Label htmlFor="reason_noted_id">
                  {t("form.reason_noted_id")}{" "}
                  <span className="text-danger">*</span>
                </Label>

                <Controller
                  control={control}
                  name="reason_noted_id"
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={t("placeholder.select", {
                        title: t("form.reason_noted_id"),
                      })}
                      isSearchable
                      className="basic-single w-100"
                      classNamePrefix="select"
                      options={optionsSelectReason}
                    />
                  )}
                />
                {errors.reason_noted_id && (
                  <MessageFormValidate error={errors.reason_noted_id.message} />
                )}
              </FormGroup>
            </Row>
            <Row>
              <FormGroup className="gy-2">
                <Label htmlFor="noted_description">
                  {t("form.noted_description")}{" "}
                  {isNoteDescriptionRequired && (
                    <span className="text-danger">*</span>
                  )}
                  <i className="text-secondary fw-normal">
                    (Giới hạn 255 kí tự)
                  </i>
                </Label>

                <Controller
                  control={control}
                  name="noted_description"
                  render={({ field }) => (
                    <TextArea
                      {...field}
                      placeholder={
                        "Họ tên - năm sinh - (gói XN đúng / tên sales khác)"
                      }
                      className="basic-single w-100"
                      rows={5}
                    />
                  )}
                />
                {errors.noted_description ? (
                  <MessageFormValidate
                    error={errors.noted_description.message}
                  />
                ) : (
                  <span>Số kí tự còn lại: {characterRemain}</span>
                )}
              </FormGroup>
            </Row>
            <Row xs={2}>
              <Col>
                <Button
                  color="danger"
                  outline
                  className="border-0"
                  size="xs"
                  onClick={toggleWarningNoteModal}
                >
                  {t("button.cancel")}
                </Button>
              </Col>
              <Col className="text-end">
                <Button
                  size="xs"
                  color="warning"
                  type="submit"
                  disabled={isLoadingNoteOrderMutate || isLoadingOrderNotes}
                >
                  Gửi lưu ý
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal>
      </>
    )
  );
};

export default WarningNote;
